import { useState, useEffect } from "react";
import { enqueueSnackbar } from 'notistack';

import { Box, Stack } from "@mui/system";
import { People, Assignment, CalendarToday } from '@mui/icons-material';
import { Card, Grid, Chip, Button, Avatar, Skeleton, Typography } from "@mui/material";

import { ReferralRequest } from "src/types/newTypes/referralAndTransferForms/referralRequest";

import Label from '../../../../components/label';
import { useRouter } from '../../../../routes/hooks';
import { useDjangoApi } from '../../../../universal-components/djangoServerUtilities/djangoHandler';

type Props = {
    providerID: string;
    clientID: string;
    closeModal: () => void;
    state: string;
}

export default function ReferralCards({providerID, clientID, state, closeModal} : Props) {
  const router = useRouter();
  const djangoApi = useDjangoApi()
  const [referralRequests, setReferralRequests] = useState<ReferralRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getReferralRequests = async() => {
      setLoading(true);
      const response = await djangoApi.get(`clients/matching/referral-requests/?provider_id=${providerID}&state=${state}&status=open`)
      if (response && response.status === 200) {
        console.log(response);
        setReferralRequests(response.data.results);
      }
      setLoading(false);
    }
    getReferralRequests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerID]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Stack spacing={2}>
        {[1, 2, 3].map((i) => (
          <Card key={i} sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="text" width={200} height={24} />
                <Skeleton variant="rectangular" width={80} height={32} />
              </Stack>
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="60%" height={20} />
            </Stack>
          </Card>
        ))}
      </Stack>
    );
  }

  const assignClient = async(referralRequestID: string) => {
    try {
      const response = await djangoApi.put(`clients/matching/assign-client/?referral_request_id=${referralRequestID}&client_id=${clientID}`);
      if(response && response.status === 200) {
        enqueueSnackbar('Client assigned')
        closeModal();
        // Take the admin back to the matching tab so they can continue their work
        router.push('/dashboard/admins/clients?status=matching');
      }
    } catch (error) {
      enqueueSnackbar('Error assigning client', {variant: 'error'});
      console.error('Error assigning client', error);
    }
  }

  return (
    <Stack spacing={3}>
      {referralRequests.length > 0 ? (
        referralRequests.map((request) => (
          <Card
            key={request.id}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              transition: 'all 0.1s ease-in-out',
              '&:hover': {
                boxShadow: (theme) => theme.shadows[8],
                transform: 'translateY(-2px)',
                borderColor: 'primary.main',
              }
            }}
          >
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid xs={12} sm={8}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        sx={{
                          bgcolor: 'primary.main',
                          width: 40,
                          height: 40
                        }}
                      >
                        <CalendarToday fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" color="text.secondary">
                          Created on {formatDate(request.created_at)}
                        </Typography>
                      </Box>
                      <Label
                        color='warning'
                        sx={{
                          px: 1,
                          py: 1,
                          borderRadius: 2,
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        <People sx={{ mr: 1, fontSize: '1rem' }} />
                        {`${request.clients_assigned.length} / ${request.num_clients_requested}`}
                      </Label>
                    </Stack>


                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                        Requirements
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {request.accepting_in_person && (
                          <Chip
                            label="In-person"
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1.5 }}
                          />
                        )}
                        {request.no_minors && (
                          <Chip
                            label="No minors"
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1.5 }}
                          />
                        )}
                        {request.no_couples_or_families && (
                          <Chip
                            label="No couples/families"
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1.5 }}
                          />
                        )}
                        {request.cash_pay_only && (
                          <Chip
                            label="Cash pay only"
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1.5 }}
                          />
                        )}
                      </Stack>
                    </Box>

                    {request.clients_requested_by_name.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                            Clients Requested by Name
                          </Typography>
                          <Stack spacing={0.5}>
                            {request.clients_requested_by_name.map((client, index) => (
                              <Typography
                                key={index}
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  pl: 2,
                                  borderLeft: '2px solid',
                                  borderColor: 'primary.light',
                                  py: 0.5
                                }}
                              >
                                {client}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                    )}
                  </Stack>
                </Grid>

                {/* Action Section */}
                <Grid xs={12} sm={4}>
                  <Stack
                    spacing={2}
                    sx={{
                      height: '100%',
                      justifyContent: 'flex-start',
                      alignItems: { xs: 'stretch', sm: 'flex-end' },
                    }}
                  >
                    <Button
                      onClick={() => assignClient(request.id)}
                      variant="contained"
                      size="large"
                      sx={{
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 2,
                        '&:hover': {
                          boxShadow: 4,
                        }
                      }}
                    >
                      Assign Client
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Card>
        ))
      ) : (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: 'grey.100',
                width: 64,
                height: 64,
                mb: 1
              }}
            >
              <Assignment sx={{ fontSize: 32, color: 'grey.500' }} />
            </Avatar>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
              No Referral Requests
            </Typography>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
